import {
  normalizeSettlementCorrectionStats,
  settlementCorrectionForRecord,
} from './raidSettlementCorrection.js';
import { findNikkeInCatalog } from '../../nikke-catalog/domain/nikkeCatalog.js';

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function memberKey(value, fallback = '') {
  return String(value || fallback || '').trim();
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

export function buildBossCorrectionStats({ records = [], bosses = [], referenceStats = null } = {}) {
  const bossNos = [...new Set([
    ...(Array.isArray(bosses) ? bosses : []).map((boss) => numberValue(boss?.bossNo ?? boss?.boss_no)).filter((bossNo) => bossNo >= 1 && bossNo <= 5),
    ...(Array.isArray(records) ? records : []).map((record) => numberValue(record?.bossNo ?? record?.boss_no)).filter((bossNo) => bossNo >= 1 && bossNo <= 5),
  ])].sort((a, b) => a - b);
  const validRecords = (Array.isArray(records) ? records : [])
    .map((record) => ({
      source: record,
      bossNo: numberValue(record?.bossNo ?? record?.boss_no),
      damageEok: numberValue(record?.damageEok ?? record?.damage_eok),
      charLevel: numberValue(record?.charLevel ?? record?.char_level, 0) || null,
    }))
    .filter((record) => record.bossNo >= 1 && record.bossNo <= 5 && record.damageEok > 0);
  const totalDamageEok = validRecords.reduce((sum, record) => sum + record.damageEok, 0);
  const attackCount = validRecords.length;
  const globalAverageDamageEok = attackCount ? totalDamageEok / attackCount : 0;
  const localByBoss = {};

  for (const bossNo of bossNos) {
    const bossRecords = validRecords.filter((record) => record.bossNo === bossNo);
    const bossDamageEok = bossRecords.reduce((sum, record) => sum + record.damageEok, 0);
    const bossAttackCount = bossRecords.length;
    const averageDamageEok = bossAttackCount ? bossDamageEok / bossAttackCount : 0;
    const rawCoefficient = averageDamageEok > 0 && globalAverageDamageEok > 0
      ? globalAverageDamageEok / averageDamageEok
      : 1;
    localByBoss[bossNo] = {
      bossNo,
      totalDamageEok: bossDamageEok,
      attackCount: bossAttackCount,
      averageDamageEok,
      rawCoefficient,
      coefficient: clamp(rawCoefficient || 1, 0.65, 1.45),
    };
  }

  const embeddedReferenceStats = referenceStats || (Array.isArray(bosses)
    ? bosses.find((boss) => boss?.settlementCorrectionStats)?.settlementCorrectionStats
    : null);
  const normalizedReference = normalizeSettlementCorrectionStats(embeddedReferenceStats);
  const coefficientForRecord = (record = {}) => {
    const bossNo = numberValue(record?.bossNo ?? record?.boss_no);
    const fallbackCoefficient = localByBoss[bossNo]?.coefficient || 1;
    return settlementCorrectionForRecord(normalizedReference, record, fallbackCoefficient);
  };
  const byBoss = {};

  for (const bossNo of bossNos) {
    const local = localByBoss[bossNo] || {
      bossNo,
      totalDamageEok: 0,
      attackCount: 0,
      averageDamageEok: 0,
      rawCoefficient: 1,
      coefficient: 1,
    };
    const bossRecords = validRecords.filter((record) => record.bossNo === bossNo);
    const applied = bossRecords.map((record) => coefficientForRecord(record.source));
    const weightedDamageEok = bossRecords.reduce(
      (sum, record, index) => sum + record.damageEok * (applied[index]?.coefficient || local.coefficient),
      0,
    );
    const emptyApplied = coefficientForRecord({ bossNo });
    const coefficient = local.totalDamageEok > 0
      ? weightedDamageEok / local.totalDamageEok
      : emptyApplied.coefficient;
    const coefficientValues = applied.length ? applied.map((item) => item.coefficient) : [coefficient];
    const referenceAllLevel = normalizedReference?.allLevelByBoss?.get(bossNo) || null;
    const levelBandKeys = new Set(applied.map((item) => item.levelBand?.key).filter(Boolean));

    byBoss[bossNo] = {
      ...local,
      coefficient,
      coefficientMin: Math.min(...coefficientValues),
      coefficientMax: Math.max(...coefficientValues),
      weightedDamageEok,
      levelBandCount: levelBandKeys.size,
      referenceAttackCount: referenceAllLevel?.attackCount || 0,
      referenceUnionCount: referenceAllLevel?.unionCount || 0,
      correctionMode: normalizedReference ? 'cross_union_level_band' : 'local_union',
    };
  }

  return {
    bossNos,
    byBoss,
    totalDamageEok,
    attackCount,
    globalAverageDamageEok,
    referenceStats: normalizedReference,
    coefficientForRecord,
  };
}

function deckItem(item = {}, catalog = null) {
  const catalogItem = findNikkeInCatalog(catalog, item);
  if (typeof item === 'string' || typeof item === 'number') {
    const code = String(item).trim();
    const canonicalCode = catalogItem?.nikkeId || code;
    return code ? { code: canonicalCode, name: catalogItem?.name || code, imageUrl: catalogItem?.imageUrl || '' } : null;
  }
  const code = String(item?.code ?? item?.nikkeId ?? item?.nikke_id ?? item?.id ?? '').trim();
  if (!code && !catalogItem?.nikkeId) return null;
  return {
    code: catalogItem?.nikkeId || code,
    name: String(item?.name ?? item?.officialName ?? catalogItem?.name ?? catalogItem?.officialName ?? code).trim() || code,
    imageUrl: String(item?.imageUrl ?? item?.image_url ?? catalogItem?.imageUrl ?? '').trim(),
  };
}

function normalizedDeck(deck = [], catalog = null) {
  return (Array.isArray(deck) ? deck : []).map((item) => deckItem(item, catalog)).filter(Boolean).slice(0, 5);
}

function buildBossNikkeUsage(records = []) {
  const usage = new Map();
  const attackCount = records.length;
  for (const record of records) {
    const seen = new Set();
    for (const item of normalizedDeck(record?.deck)) {
      if (seen.has(item.code)) continue;
      seen.add(item.code);
      const current = usage.get(item.code) || { ...item, count: 0 };
      current.count += 1;
      if (!current.imageUrl && item.imageUrl) current.imageUrl = item.imageUrl;
      if ((!current.name || current.name === current.code) && item.name) current.name = item.name;
      usage.set(item.code, current);
    }
  }
  return [...usage.values()]
    .map((item) => ({ ...item, usagePercent: attackCount > 0 ? (item.count / attackCount) * 100 : 0 }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'ko-KR'))
    .slice(0, 5);
}

function normalizePhase(phase = {}) {
  return {
    phaseNo: numberValue(phase.phaseNo),
    maxHpEok: phase.maxHpEok === null || phase.maxHpEok === undefined ? null : numberValue(phase.maxHpEok),
    damageEok: numberValue(phase.damageEok),
    remainingHpEok: phase.remainingHpEok === null || phase.remainingHpEok === undefined ? null : numberValue(phase.remainingHpEok),
    remainingPercent: numberValue(phase.remainingPercent),
    progressPercent: numberValue(phase.progressPercent),
    attackCount: numberValue(phase.attackCount),
    cleared: Boolean(phase.cleared),
    isInfinite: Boolean(phase.isInfinite),
  };
}

function emptyMemberRow(key, nickname) {
  return {
    memberKey: key,
    nickname: String(nickname || '닉네임 확인 불가'),
    attacks: [],
    attackCount: 0,
    totalDamageEok: 0,
    weightedDamageEok: 0,
    killCount: 0,
    bossDamage: new Map(),
    bossWeightedDamage: new Map(),
  };
}

function buildPhaseDistribution(attacks = [], configuredPhases = []) {
  const phaseNos = new Set([
    ...configuredPhases.map((phase) => numberValue(phase.phaseNo)).filter(Boolean),
    ...attacks.map((attack) => numberValue(attack.phaseNo)).filter(Boolean),
  ]);
  return [...phaseNos].sort((a, b) => a - b).map((phaseNo) => {
    const rows = attacks.filter((attack) => attack.phaseNo === phaseNo);
    return {
      phaseNo,
      attackCount: rows.length,
      damageEok: rows.reduce((sum, attack) => sum + attack.damageEok, 0),
      percent: attacks.length ? (rows.length / attacks.length) * 100 : 0,
    };
  });
}

export function buildRaidSettlement({ records = [], members = [], bosses = [], correctionStats = null, catalog = null } = {}) {
  const normalizedBossRows = (Array.isArray(bosses) ? bosses : [])
    .map((boss) => ({
      ...boss,
      bossNo: numberValue(boss.bossNo),
      phases: (Array.isArray(boss.phases) ? boss.phases : []).map(normalizePhase),
      currentPhase: numberValue(boss.currentPhase, 1),
      maxHpEok: boss.maxHpEok === null || boss.maxHpEok === undefined ? null : numberValue(boss.maxHpEok),
      remainingHpEok: boss.remainingHpEok === null || boss.remainingHpEok === undefined ? null : numberValue(boss.remainingHpEok),
      remainingPercent: numberValue(boss.remainingPercent),
      progressPercent: numberValue(boss.progressPercent),
      isInfinite: Boolean(boss.isInfinite),
      isActive: boss.isActive !== false,
      isCleared: Boolean(boss.isCleared),
    }))
    .filter((boss) => boss.bossNo >= 1 && boss.bossNo <= 5)
    .sort((a, b) => a.bossNo - b.bossNo);
  const correction = buildBossCorrectionStats({ records, bosses: normalizedBossRows, referenceStats: correctionStats });
  const bossRows = normalizedBossRows.map((boss) => ({
    ...boss,
    coefficient: correction.byBoss[boss.bossNo]?.coefficient || 1,
    coefficientMin: correction.byBoss[boss.bossNo]?.coefficientMin || 1,
    coefficientMax: correction.byBoss[boss.bossNo]?.coefficientMax || 1,
    rawCoefficient: correction.byBoss[boss.bossNo]?.rawCoefficient || 1,
    referenceAttackCount: correction.byBoss[boss.bossNo]?.referenceAttackCount || 0,
    referenceUnionCount: correction.byBoss[boss.bossNo]?.referenceUnionCount || 0,
    correctionMode: correction.byBoss[boss.bossNo]?.correctionMode || 'local_union',
  }));
  const bossMap = new Map(bossRows.map((boss) => [boss.bossNo, boss]));
  const rows = new Map();

  for (const member of Array.isArray(members) ? members : []) {
    const key = memberKey(member.openid ?? member.memberOpenid, member.nickname);
    if (key) rows.set(key, emptyMemberRow(key, member.nickname));
  }

  for (const record of Array.isArray(records) ? records : []) {
    const key = memberKey(record.memberOpenid, record.nickname);
    if (!key) continue;
    const current = rows.get(key) || emptyMemberRow(key, record.nickname);
    const bossNo = numberValue(record.bossNo);
    const damageEok = numberValue(record.damageEok);
    const appliedCorrection = correction.coefficientForRecord(record);
    const coefficient = appliedCorrection.coefficient;
    const weightedDamageEok = damageEok * coefficient;
    current.nickname = String(record.nickname || current.nickname);
    current.attacks.push({
      attackNo: numberValue(record.attackNo) || current.attacks.length + 1,
      bossNo,
      bossName: String(record.bossName || bossMap.get(bossNo)?.bossName || `보스 ${bossNo}`),
      phaseNo: numberValue(record.phaseNo),
      damageEok,
      coefficient,
      correctionSource: appliedCorrection.source,
      levelBand: appliedCorrection.levelBand,
      weightedDamageEok,
      isKill: Boolean(record.isKill),
      attackedAt: record.attackedAt || null,
      charLevel: numberValue(record.charLevel, 0) || null,
      nickname: String(record.nickname || current.nickname),
      memberKey: key,
      deck: normalizedDeck(record.deck, catalog),
    });
    current.attackCount += 1;
    current.totalDamageEok += damageEok;
    current.weightedDamageEok += weightedDamageEok;
    current.killCount += record.isKill ? 1 : 0;
    current.bossDamage.set(bossNo, numberValue(current.bossDamage.get(bossNo)) + damageEok);
    current.bossWeightedDamage.set(bossNo, numberValue(current.bossWeightedDamage.get(bossNo)) + weightedDamageEok);
    rows.set(key, current);
  }

  const totalDamageEok = [...rows.values()].reduce((sum, row) => sum + row.totalDamageEok, 0);
  const totalWeightedDamageEok = [...rows.values()].reduce((sum, row) => sum + row.weightedDamageEok, 0);
  const ranking = [...rows.values()]
    .map((row) => ({
      ...row,
      attacks: row.attacks.slice().sort((a, b) => a.attackNo - b.attackNo),
      remainingAttacks: Math.max(3 - Math.min(row.attackCount, 3), 0),
      contributionPercent: totalWeightedDamageEok > 0 ? (row.weightedDamageEok / totalWeightedDamageEok) * 100 : 0,
      rawContributionPercent: totalDamageEok > 0 ? (row.totalDamageEok / totalDamageEok) * 100 : 0,
      bossDamageRows: bossRows.map((boss) => {
        const damageEok = numberValue(row.bossDamage.get(boss.bossNo));
        const weightedDamageEok = numberValue(row.bossWeightedDamage.get(boss.bossNo));
        return {
          bossNo: boss.bossNo,
          bossName: String(boss.bossName || `보스 ${boss.bossNo}`),
          coefficient: damageEok > 0 ? weightedDamageEok / damageEok : boss.coefficient,
          damageEok,
          weightedDamageEok,
        };
      }),
    }))
    .sort((a, b) => b.weightedDamageEok - a.weightedDamageEok || b.totalDamageEok - a.totalDamageEok || b.attackCount - a.attackCount || a.nickname.localeCompare(b.nickname, 'ko'))
    .map((row, index) => ({ ...row, rank: index + 1 }));

  const allAttacks = ranking.flatMap((row) => row.attacks);
  const bossTotals = bossRows.map((boss) => {
    const attacks = allAttacks.filter((attack) => attack.bossNo === boss.bossNo).sort((a, b) => b.damageEok - a.damageEok || a.attackNo - b.attackNo);
    const total = attacks.reduce((sum, attack) => sum + attack.damageEok, 0);
    const weightedTotal = attacks.reduce((sum, attack) => sum + attack.weightedDamageEok, 0);
    const coefficientValues = attacks.length ? attacks.map((attack) => attack.coefficient) : [boss.coefficient];
    const topAttack = attacks[0] || null;
    return {
      bossNo: boss.bossNo,
      bossName: String(boss.bossName || `보스 ${boss.bossNo}`),
      element: String(boss.element || ''),
      imageUrl: String(boss.imageUrl || ''),
      comment: String(boss.comment || ''),
      coefficient: total > 0 ? weightedTotal / total : boss.coefficient,
      coefficientMin: Math.min(...coefficientValues),
      coefficientMax: Math.max(...coefficientValues),
      rawCoefficient: boss.rawCoefficient,
      correctionMode: boss.correctionMode,
      referenceAttackCount: boss.referenceAttackCount,
      referenceUnionCount: boss.referenceUnionCount,
      attackCount: attacks.length,
      totalDamageEok: total,
      averageDamageEok: attacks.length ? total / attacks.length : 0,
      topDamageEok: topAttack?.damageEok || 0,
      topAttack,
      weightedDamageEok: weightedTotal,
      weightedContributionPercent: totalWeightedDamageEok > 0 ? (weightedTotal / totalWeightedDamageEok) * 100 : 0,
      rawContributionPercent: totalDamageEok > 0 ? (total / totalDamageEok) * 100 : 0,
      killCount: attacks.filter((attack) => attack.isKill).length,
      mostUsedNikkes: buildBossNikkeUsage(attacks),
      phaseDistribution: buildPhaseDistribution(attacks, boss.phases),
      phases: boss.phases,
      currentPhase: boss.currentPhase,
      maxHpEok: boss.maxHpEok,
      remainingHpEok: boss.remainingHpEok,
      remainingPercent: boss.remainingPercent,
      progressPercent: boss.progressPercent,
      isInfinite: boss.isInfinite,
      isActive: boss.isActive,
      isCleared: boss.isCleared,
      attacks,
    };
  });

  const attackCount = ranking.reduce((sum, row) => sum + row.attackCount, 0);
  return {
    ranking,
    bossTotals,
    totalDamageEok,
    totalWeightedDamageEok,
    attackCount,
    participantCount: ranking.filter((row) => row.attackCount > 0).length,
    completedMemberCount: ranking.filter((row) => row.attackCount >= 3).length,
    averageDamageEok: attackCount > 0 ? totalDamageEok / attackCount : 0,
    globalAverageDamageEok: correction.globalAverageDamageEok,
    correctionScope: {
      mode: correction.referenceStats ? 'cross_union_level_band' : 'local_union',
      unionCount: correction.referenceStats?.unionCount || 0,
      attackCount: correction.referenceStats?.attackCount || 0,
      levelBandSize: correction.referenceStats?.levelBandSize || 20,
    },
  };
}
